<template>
    <div>
        <ModeSwitcher
            ref="modeSwitcher"
            :main-mode-text="$t('create')"
            :create-mode-text="useCreateModeText"
            :edit-mode-text="useEditModeText"
            :edit-mode-only="isHotelDescriptionMode"
            @reset="editSecondaryService = null"
        >
            <template v-slot:btn-block>
                <div class="full-width d-flex align-center">
                    <v-spacer />
                    <FilterStatusToggle v-model="activeFilter" />
                </div>
            </template>
            <template v-slot:main-mode>
                <v-data-table
                    :headers="useHeaders"
                    :items="useFilteredServices"
                    :items-per-page="-1"
                    :items-per-page-options="[-1]"
                    :loading="isLoading"
                    :items-length="useFilteredServices.length"
                    :custom-key-sort="{
                        name: (a, b) => localized(a).localeCompare(localized(b)),
                    }"
                    :sort-by="[{key: 'name', order: 'asc'}]"
                    must-sort
                    density="compact"
                    color="primary"
                    class="elevation-1 table-server"
                    data-cy="guests-table"
                >
                    <template v-slot:item.name="{item}">
                        <div class="text-link" @click="editService(item)">
                            {{ localized(item.name) || item.id }}
                        </div>
                        <div v-if="item.defaultService || item.useAsDefault" class="text-medium-emphasis">
                            ({{ $t('product.info.default_board') }})
                        </div>
                    </template>
                    <template v-slot:item.serviceTypeId="{item}">
                        {{ getSecondaryServiceType(item.serviceTypeId) }}
                    </template>
                    <template v-slot:item.active="{item}">
                        {{ item.active ? $t('active') : $t('inactive') }}
                    </template>
                </v-data-table>
            </template>
            <template v-slot:additional-mode>
                <template v-if="isOwnHotel">
                    <ProductHotelMealTypeForm
                        :edit-secondary-service="editSecondaryService"
                        :secondary-types="secondaryTypes"
                        @save="save()"
                        @cancel="modeSwitcher.goToTable()"
                    />
                </template>
                <InfoSecondaryServicesForm
                    v-else
                    :edit-secondary-service="editSecondaryService"
                    :contract-id="contractId"
                    :service-type="serviceType"
                    :secondary-types="secondaryTypes"
                    @save="save()"
                    @cancel="modeSwitcher.goToTable()"
                />
            </template>
        </ModeSwitcher>
    </div>
</template>

<script setup lang="ts">
    import InfoSecondaryServicesForm from '~/components/products/information/InfoSecondaryServicesForm.vue'
    import {contractServiceType} from '~/utils/types/types'
    import {useHotelInfo} from '~/composables/form-helpers'
    import ProductHotelMealTypeForm from '~/components/products/hotels/ProductHotelMealTypeForm.vue'

    const localized = useLocalized()

    type HeaderItemType = {
        title: string
        value: string
        width?: string
        minWidth?: string
    }

    const props = defineProps<{
        contractId?: number
        serviceType?: string
    }>()

    const isLoading = ref<boolean>(false)
    const secondaryServices = ref<any[]>([])
    const modeSwitcher = ref(null)
    const editSecondaryService = ref(null)
    const isOwnHotel = ref<boolean>(props.serviceType === contractServiceType.ownHotel)
    const isHotelDescriptionMode: boolean = inject('isHotelDescriptionMode', false)
    const secondaryTypes = ref([])
    const activeFilter = ref<boolean>(true)

    const app = useNuxtApp()
    const {t} = useI18n()

    onMounted(async () => {
        if (isOwnHotel.value) {
            await loadMealType()
        } else await loadData()

        if (props.serviceType !== contractServiceType.ownVisa && !isOwnHotel.value) {
            await loadSecondaryServiceType()
            EventBus.$on(CHANGE_LOCALE_EVENT, reload)
        }
        if (isOwnHotel.value) {
            await loadHotelMealTypesDictionaryInfo()
            EventBus.$on(CHANGE_LOCALE_EVENT, loadHotelMealTypesDictionaryInfo)
        }
    })

    onBeforeUnmount(async () => {
        EventBus.$off(CHANGE_LOCALE_EVENT, reload)
        EventBus.$off(CHANGE_LOCALE_EVENT, loadHotelMealTypesDictionaryInfo)
    })

    async function reload(): Promise<void> {
        await loadSecondaryServiceType()
    }

    if (isOwnHotel.value) {
        watch(useHotelInfo, async () => {
            if (isOwnHotel.value) {
                await loadMealType()
                modeSwitcher.value.goToTable()
            }
        })
    }

    async function loadMealType(): Promise<void> {
        try {
            isLoading.value = true
            const {mealTypes} = await app.$api.hotelDescriptionMealType.get(supplierStore.hotelInfo.hotelId, {
                locale: 'all',
            })
            secondaryServices.value = mealTypes
        } catch (e) {
            secondaryServices.value = []
        } finally {
            isLoading.value = false
        }
    }

    async function loadHotelMealTypesDictionaryInfo(): Promise<void> {
        try {
            const res = await app.$api.hotelMealTypesDictionaryInfo.get()
            secondaryTypes.value = res.sort((a, b) => a.name.localeCompare(b.name))
        } catch (e) {
            secondaryTypes.value = []
        }
    }

    async function loadData(): Promise<void> {
        try {
            isLoading.value = true
            const rq = {contractId: props.contractId}
            const {services} = await app.$api.ownProductSecondaryServices.get(rq)
            secondaryServices.value = services
        } catch (e) {
            secondaryServices.value = []
        } finally {
            isLoading.value = false
        }
    }

    async function loadSecondaryServiceType(): Promise<void> {
        try {
            isLoading.value = true
            const {object} = await app.$api.ownProductSecondaryServiceType.get(props.serviceType)
            secondaryTypes.value = object.sort((a, b) => a.typeName.localeCompare(b.typeName))
        } catch (e) {
            secondaryTypes.value = []
        } finally {
            isLoading.value = false
        }
    }

    const getSecondaryServiceType = serviceId => {
        const secondaryType = secondaryTypes.value.find(({id}) => id === serviceId)
        return secondaryType?.typeName || secondaryType?.id
    }

    const save = async (): Promise<void> => {
        if (isOwnHotel.value) {
            await loadMealType()
        } else await loadData()
        modeSwitcher.value.goToTable()
    }

    const useHeaders: ComputedRef<HeaderItemType[]> = computed(() => {
        if (props.serviceType === contractServiceType.ownVisa || isOwnHotel.value) {
            return [
                {title: t('name'), value: 'name', minWidth: '120px', sortable: true},
                {title: t('status'), value: 'active', width: '140px', minWidth: '140px'},
            ]
        }
        return [
            {title: t('name'), value: 'name', minWidth: '120px', sortable: true},
            {title: t('type'), value: 'serviceTypeId'},
            {title: t('status'), value: 'active', width: '140px', minWidth: '140px'},
        ]
    })

    const useCreateModeText = computed(() => {
        switch (props.serviceType) {
            case contractServiceType.ownHotel:
                return t('product.info.create_meal_type')
            case contractServiceType.ownVisa:
                return t('product.info.create_duration_of_stay')
            default:
                return t('product.info.create_board_type')
        }
    })

    const useEditModeText = computed(() => {
        switch (props.serviceType) {
            case contractServiceType.ownHotel:
                return t('product.info.edit_meal_type')
            case contractServiceType.ownVisa:
                return t('product.info.edit_duration_of_stay')
            default:
                return t('product.info.edit_board_type')
        }
    })

    const editService = (service): void => {
        modeSwitcher.value.edit()
        editSecondaryService.value = service
    }

    const useFilteredServices = computed(() => {
        return secondaryServices.value.filter(({active}) => activeFilter.value === active)
    })
</script>
